import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Badge,
  Box,
  ExpandableSection,
  Textarea,
  Modal,
  Table,
} from '@amzn/awsui-components-console';
import CodeView from '@amzn/awsui-code-view-components-console/code-view';
import { createHighlight } from '@amzn/awsui-code-view-components-console/highlight';
import { cleanMermaidContent } from '../../utils/cleanMermaidContent';
import { SqlHighlightRules } from 'ace-code/src/mode/sql_highlight_rules';
import { PythonHighlightRules } from 'ace-code/src/mode/python_highlight_rules';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ArtifactSection } from '../../types/agent-messages';
import { sanitizeSvg } from '../../utils/sanitizeSvg';

export interface ArtifactButton {
  label: string;
  style: 'primary' | 'normal';
  action: string;
  onClick?: () => void;
}

export interface ArtifactReviewCardProps {
  jobId: string;
  jobName?: string;
  generateStatus: string;
  sections: ArtifactSection[];
  buttons: ArtifactButton[];
  onButtonClick?: (action: string) => void;
  onFeedbackSubmit?: (feedbackText: string) => void;
}

const STATUS_COLOR_MAP: Record<string, 'blue' | 'green' | 'red' | 'grey'> = {
  COMPLETED: 'green',
  GENERATION_FAILED: 'red',
  IN_PROGRESS: 'blue',
  PENDING: 'grey',
};

const sqlHighlight = createHighlight(new SqlHighlightRules());
const pythonHighlight = createHighlight(new PythonHighlightRules());

function getHighlightForLanguage(language?: string) {
  switch (language?.toLowerCase()) {
    case 'sql':
      return sqlHighlight;
    case 'python':
    case 'pyspark':
      return pythonHighlight;
    default:
      return sqlHighlight;
  }
}

function getLanguageLabel(language?: string): string {
  switch (language?.toLowerCase()) {
    case 'sql':
      return 'SQL';
    case 'python':
      return 'Python';
    case 'pyspark':
      return 'PySpark';
    default:
      return language?.toUpperCase() || 'Code';
  }
}

// Using shared cleanMermaidContent from utils/cleanMermaidContent.ts (imported above)

// Custom Mermaid renderer using dynamic import (same approach as JsonDataViewer)
const MermaidRenderer: React.FC<{ content: string }> = ({ content }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const renderMermaid = async () => {
      try {
        // Dynamically import mermaid
        const mermaid = (await import('mermaid')).default;
        
        // Initialize mermaid
        mermaid.initialize({
          startOnLoad: false,
          theme: 'default',
          securityLevel: 'strict',
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: 'basis'
          }
        });

        // Generate unique ID
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Render the diagram
        const { svg: renderedSvg } = await mermaid.render(id, content);
        setSvg(renderedSvg);
        setError('');
      } catch (err: any) {
        console.error('Error rendering mermaid diagram:', err);
        setError(err.message || 'Failed to render diagram');
        setSvg('');
      }
    };

    if (content) {
      renderMermaid();
    }
  }, [content]);

  if (error) {
    return (
      <Box color="text-status-error" padding="m">
        <div>Error rendering diagram: {error}</div>
        <pre style={{ fontSize: '12px', marginTop: '10px', whiteSpace: 'pre-wrap' }}>{content}</pre>
      </Box>
    );
  }

  if (!svg) {
    return <Box textAlign="center" padding="l">Loading diagram...</Box>;
  }

  return (
    <div 
      ref={containerRef}
      dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
      style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
    />
  );
};

const MermaidSection: React.FC<{ content: string }> = ({ content }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const cleanedContent = cleanMermaidContent(content);

  const handleDownload = () => {
    const blob = new Blob([cleanedContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'diagram.mmd';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!cleanedContent) {
    return (
      <div style={{ padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
        <p style={{ color: '#666' }}>No diagram content available.</p>
      </div>
    );
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: '4px', right: '4px', zIndex: 1, display: 'flex', gap: '4px' }}>
          <Button
            iconName="download"
            variant="icon"
            onClick={handleDownload}
            ariaLabel="Download mermaid source"
          />
          <Button
            iconName="expand"
            variant="icon"
            onClick={() => setIsFullscreen(true)}
            ariaLabel="View diagram fullscreen"
          />
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          overflow: 'auto', 
          maxHeight: '400px',
          backgroundColor: '#ffffff',
          borderRadius: '4px',
          padding: '12px'
        }}>
          <MermaidRenderer content={cleanedContent} />
        </div>
      </div>

      <Modal
        visible={isFullscreen}
        onDismiss={() => setIsFullscreen(false)}
        closeAriaLabel="Close fullscreen view"
        size="max"
        header="Data Flow Diagram"
      >
        <div
          style={{
            width: '100%',
            height: '70vh',
            overflow: 'auto',
            backgroundColor: '#ffffff',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MermaidRenderer content={cleanedContent} />
        </div>
      </Modal>
    </>
  );
};

const CodeSection: React.FC<{ content: string; language?: string }> = ({ content, language }) => {
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch { /* no-op */ }
  };

  const handleDownload = () => {
    const ext = language?.toLowerCase() === 'sql' ? 'sql' : 'py';
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `artifact.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px', marginBottom: '4px' }}>
        <Button
          iconName={copyState === 'copied' ? 'check' : 'copy'}
          variant="icon"
          onClick={handleCopy}
          ariaLabel="Copy code"
        />
        <Button
          iconName="download"
          variant="icon"
          onClick={handleDownload}
          ariaLabel="Download code"
        />
      </div>
      <CodeView
        content={content}
        highlight={getHighlightForLanguage(language)}
        lineNumbers={content.split('\n').length > 5}
        wrapLines
      />
    </div>
  );
};

const markdownComponents = {
  p: ({ children }: any) => <div style={{ margin: '4px 0' }}>{children}</div>,
  ul: ({ children }: any) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ul>,
  ol: ({ children }: any) => <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ol>,
  li: ({ children }: any) => <li style={{ marginBottom: '2px', fontSize: '13px' }}>{children}</li>,
};

export const ArtifactReviewCard: React.FC<ArtifactReviewCardProps> = ({
  jobName,
  generateStatus,
  sections,
  buttons,
  onButtonClick,
  onFeedbackSubmit,
}) => {
  const [clickedAction, setClickedAction] = useState<string | null>(null);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  const handleClick = (btn: ArtifactButton) => {
    if (btn.action === 'feedback_artifacts') {
      setShowFeedbackInput(true);
      return;
    }
    setClickedAction(btn.action);
    btn.onClick?.();
    onButtonClick?.(btn.action);
  };

  const handleFeedbackSubmit = () => {
    const trimmed = feedbackText.trim();
    if (!trimmed) return;
    if (onFeedbackSubmit) {
      onFeedbackSubmit(trimmed);
    } else {
      onButtonClick?.(`feedback: ${trimmed}`);
    }
    setFeedbackText('');
    setShowFeedbackInput(false);
  };

  const badgeColor = STATUS_COLOR_MAP[generateStatus?.toUpperCase()] ?? 'grey';

  const renderSection = (section: ArtifactSection, idx: number) => {
    const sectionContent = (() => {
      switch (section.type) {
        case 'text':
          return <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{section.content}</ReactMarkdown>;
        case 'mermaid':
          return <MermaidSection content={section.content} />;
        case 'codeBlock':
          return <CodeSection content={section.content} language={section.language} />;
        case 'table':
          if (section.headers && section.rows) {
            return (
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Table
                  resizableColumns
                  columnDefinitions={section.headers.map((h, i) => ({
                    id: `col-${i}`,
                    header: h,
                    cell: (item: string[]) => item[i] ?? '',
                    minWidth: 150,
                  }))}
                  items={section.rows}
                  variant="embedded"
                  wrapLines
                  empty={
                    <Box textAlign="center" color="text-status-inactive" padding="s">
                      No data rows — headers only
                    </Box>
                  }
                />
              </div>
            );
          }
          return <pre style={{ whiteSpace: 'pre-wrap', fontSize: '13px' }}>{section.content}</pre>;
        default:
          return <pre style={{ whiteSpace: 'pre-wrap', fontSize: '13px' }}>{section.content}</pre>;
      }
    })();

    return (
      <ExpandableSection
        key={idx}
        headerText={
          section.type === 'codeBlock' && section.language
            ? `${section.title} (${getLanguageLabel(section.language)})`
            : section.title
        }
        defaultExpanded
        variant="footer"
      >
        {sectionContent}
      </ExpandableSection>
    );
  };

  return (
    <div className="artifact-review-card">
      <Container
        header={
          <Header
            variant="h3"
            info={<Badge color={badgeColor}>{generateStatus}</Badge>}
            description={jobName ? `Job: ${jobName}` : undefined}
          >
            Artifact Review
          </Header>
        }
      >
        <SpaceBetween size="m">
          {sections.map((section, idx) => renderSection(section, idx))}

          {showFeedbackInput && (
            <div>
              <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>
                Provide your feedback:
              </Box>
              <Textarea
                value={feedbackText}
                onChange={({ detail }) => setFeedbackText(detail.value)}
                placeholder="Describe the changes you'd like..."
                rows={3}
              />
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <Button
                  variant="link"
                  onClick={() => {
                    setShowFeedbackInput(false);
                    setFeedbackText('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleFeedbackSubmit}
                  disabled={!feedbackText.trim()}
                >
                  Submit Feedback
                </Button>
              </div>
            </div>
          )}

          {buttons.length > 0 && !showFeedbackInput && (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {buttons.map((btn, i) => (
                <Button
                  key={i}
                  variant={btn.style === 'primary' ? 'primary' : 'normal'}
                  onClick={() => handleClick(btn)}
                  disabled={clickedAction !== null}
                >
                  {btn.label}
                </Button>
              ))}
            </div>
          )}
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default ArtifactReviewCard;
